import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import Layout from "../layouts/layout";
import AUTOMATIONS from "../api/services/AUTOMATIONS";
import AutomationCard from "../containers/_automations/automationCard";
import CategorySection from "../containers/_automations/categorySection";
import AutomationEditor from "../containers/_automations/automationEditor";
import styles from "../styles/_screens/automations.module.scss";

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-4-4" />
  </svg>
);

const AutomationIcon = () => (
  <svg viewBox="0 0 28 28" aria-hidden="true">
    <path d="M15.8 2.8 6.4 15h6.1l-.8 10.2L21.6 12h-6.2l.4-9.2Z" />
  </svg>
);

export default function Automations() {
  const [automations, setAutomations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [autoRes, templRes] = await Promise.all([
        AUTOMATIONS.getAll(),
        AUTOMATIONS.getTemplates(),
      ]);

      const autoData = autoRes?.data || autoRes;
      const list = Array.isArray(autoData) ? autoData :
                   Array.isArray(autoData?.automations) ? autoData.automations :
                   [];
      setAutomations(list);

      const templData = templRes?.data || templRes;
      setCategories(templData?.categories || []);
    } catch (err) {
      toast.error("שגיאה בטעינת אוטומציות");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = async (id) => {
    setAutomations(prev => prev.map(a => a._id === id ? { ...a, isActive: !a.isActive } : a));
    try {
      await AUTOMATIONS.toggle(id);
    } catch {
      toast.error("שגיאה בעדכון");
      fetchData();
    }
  };

  const handleEdit = (automation) => {
    setEditingAutomation(automation);
    setEditorOpen(true);
  };

  const handleSave = async (updates) => {
    if (!editingAutomation) return;
    setSaving(true);
    try {
      await AUTOMATIONS.update(editingAutomation._id, updates);
      toast.success("האוטומציה עודכנה");
      setEditorOpen(false);
      setEditingAutomation(null);
      fetchData();
    } catch {
      toast.error("שגיאה בשמירה");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("למחוק את האוטומציה?")) return;
    try {
      await AUTOMATIONS.remove(id);
      toast.success("האוטומציה נמחקה");
      fetchData();
    } catch {
      toast.error("שגיאה במחיקה");
    }
  };

  const handleActivateTemplate = async (templateId) => {
    setActivating(true);
    try {
      const res = await AUTOMATIONS.createFromTemplate(templateId);
      const created = res?.data?.automation || res?.automation;
      toast.success("האוטומציה הופעלה");
      if (created) {
        setEditingAutomation(created);
        setEditorOpen(true);
      }
      fetchData();
    } catch {
      toast.error("שגיאה בהפעלה");
    } finally {
      setActivating(false);
    }
  };

  const activeCount = automations.filter(item => item.isActive).length;
  const inactiveCount = automations.length - activeCount;
  const availableTemplates = categories.reduce(
    (total, category) => total + category.templates.filter(template => !template.isActivated).length,
    0
  );

  const filteredAutomations = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("he");
    return automations.filter(automation => {
      const statusMatches = statusFilter === "all" ||
        (statusFilter === "active" && automation.isActive) ||
        (statusFilter === "inactive" && !automation.isActive);
      if (!statusMatches) return false;
      if (!normalizedQuery) return true;

      const searchableText = [
        automation.name,
        automation.description,
        automation.triggerType,
        ...(automation.actions || []).map(action => action.content),
      ].filter(Boolean).join(" ").toLocaleLowerCase("he");

      return searchableText.includes(normalizedQuery);
    });
  }, [automations, query, statusFilter]);

  const hasUnactivated = availableTemplates > 0;

  return (
    <Layout>
      <div className={styles.pageShell}>
        <div className={styles.container}>
          <section className={styles.hero} aria-labelledby="automations-title">
            <div className={styles.heroCopy}>
              <span className={styles.eyebrow}>מרכז האוטומציות</span>
              <h1 id="automations-title" className={styles.pageTitle}>העסק עובד גם כשאתם עסוקים</h1>
              <p className={styles.pageSubtitle}>
                הגדירו הודעות, תזכורות ופעולות אוטומטיות שחוסכות זמן ושומרות על קשר רציף עם הלקוחות.
              </p>
            </div>
            <div className={styles.heroIcon} aria-hidden="true"><AutomationIcon /></div>
          </section>

          {loading ? (
            <div className={styles.loadingGrid} aria-label="טוען אוטומציות">
              {[1, 2, 3, 4].map(item => <div key={item} className={styles.skeletonCard} />)}
            </div>
          ) : (
            <>
              <section className={styles.stats} aria-label="סיכום אוטומציות">
                <div className={styles.statCard}>
                  <span className={`${styles.statDot} ${styles.statDotActive}`} />
                  <div><strong>{activeCount}</strong><span>אוטומציות פעילות</span></div>
                </div>
                <div className={styles.statCard}>
                  <span className={`${styles.statDot} ${styles.statDotInactive}`} />
                  <div><strong>{inactiveCount}</strong><span>אוטומציות מושהות</span></div>
                </div>
                <div className={styles.statCard}>
                  <span className={`${styles.statDot} ${styles.statDotSuggested}`} />
                  <div><strong>{availableTemplates}</strong><span>הצעות מוכנות להפעלה</span></div>
                </div>
              </section>

              {automations.length > 0 && (
                <section className={styles.activeSection} aria-labelledby="active-automations-title">
                  <div className={styles.sectionHeading}>
                    <div>
                      <h2 id="active-automations-title" className={styles.sectionTitle}>האוטומציות שלי</h2>
                      <p>שליטה, עריכה ומעקב במקום אחד</p>
                    </div>
                    <span className={styles.resultCount}>{filteredAutomations.length} מתוך {automations.length}</span>
                  </div>

                  <div className={styles.toolbar}>
                    <label className={styles.searchBox}>
                      <SearchIcon />
                      <input
                        type="search"
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        placeholder="חיפוש לפי שם או תוכן הודעה"
                        aria-label="חיפוש אוטומציות"
                      />
                    </label>
                    <div className={styles.filters} aria-label="סינון לפי סטטוס">
                      {[
                        { id: "all", label: "הכול" },
                        { id: "active", label: "פעילות" },
                        { id: "inactive", label: "מושהות" },
                      ].map(filter => (
                        <button
                          key={filter.id}
                          type="button"
                          className={statusFilter === filter.id ? styles.filterActive : ""}
                          onClick={() => setStatusFilter(filter.id)}
                          aria-pressed={statusFilter === filter.id}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {filteredAutomations.length > 0 ? (
                    <div className={styles.grid}>
                      {filteredAutomations.map(automation => (
                        <AutomationCard
                          key={automation._id}
                          automation={automation}
                          onToggle={handleToggle}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noResults}>
                      <SearchIcon />
                      <strong>לא נמצאו אוטומציות מתאימות</strong>
                      <span>נסו לשנות את החיפוש או לבחור סינון אחר.</span>
                      <button type="button" onClick={() => { setQuery(""); setStatusFilter("all"); }}>ניקוי הסינון</button>
                    </div>
                  )}
                </section>
              )}

              {hasUnactivated && (
                <section className={styles.suggestions} aria-labelledby="suggestions-title">
                  <div className={styles.suggestionsHeader}>
                    <div className={styles.sparkIcon} aria-hidden="true">
                      <svg viewBox="0 0 24 24"><path d="m12 3 1.4 4.1L17.5 8.5l-4.1 1.4L12 14l-1.4-4.1-4.1-1.4 4.1-1.4L12 3Z"/><path d="m18.5 14 .8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z"/></svg>
                    </div>
                    <div>
                      <span className={styles.eyebrow}>רעיונות לעסק</span>
                      <h2 id="suggestions-title" className={styles.sectionTitle}>אוטומציות מומלצות להפעלה</h2>
                      <p>בחרו תבנית מוכנה, התאימו את הנוסח והתחילו לחסוך זמן.</p>
                    </div>
                  </div>
                  <div className={styles.categories}>
                    {categories.map(category => (
                      <CategorySection
                        key={category.id}
                        category={category}
                        onActivate={handleActivateTemplate}
                        activating={activating}
                      />
                    ))}
                  </div>
                </section>
              )}

              {automations.length === 0 && !hasUnactivated && (
                <div className={styles.empty}>
                  <div className={styles.emptyIcon}><AutomationIcon /></div>
                  <h2>עדיין אין אוטומציות</h2>
                  <p>ברגע שיהיו תבניות זמינות, תוכלו להפעיל אותן מכאן.</p>
                </div>
              )}
            </>
          )}

          {editorOpen && editingAutomation && (
            <AutomationEditor
              automation={editingAutomation}
              onClose={() => { setEditorOpen(false); setEditingAutomation(null); }}
              onSave={handleSave}
              saving={saving}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}
